'use strict'

import { useState } from 'react'
import axios from 'axios'
import {
  CircularProgress
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import CheckIcon from '@material-ui/icons/Check'
import BarChartIcon from '@material-ui/icons/BarChart'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { parseYmd, getDayName, getWeekTypeForDate } from './scheduleUtils'
import { formatMinutes, formatDeltaMinutes } from './mapUtils'
import RouteStopsList from './RouteStopsList'
import CandidateCard from './CandidateCard'

const WEEKLY_OT_THRESHOLD_MIN = 2400
const DAILY_OT_THRESHOLD_MIN = 480

function capColor (pct) {
  if (pct >= 100) return '#a9341d'
  if (pct >= 85) return '#b35d1a'
  return '#2f7a3e'
}

/**
 * Desert Flora styled route sidebar — 4-zone layout:
 * 1. Sticky route header: color dot + name + crew/day + capacity gauge + "Capacity details" + "Approve Day"
 * 2. Segmented control: Build (count) / Stops (count)
 * 3. Scrollable work area: candidate cards or ordered stops
 * 4. Footer: counts
 */
export default function RouteSidebar ({
  isFleetMode, focusDate, focusCrewId,
  focusRouteId, focusCrewName,
  hoursMode, setHoursMode,
  setFocusRouteId, setFocusCrewId,
  fleetDayStats, capacityMatrix,
  loadingFocusContextStats,
  contextSummary, contextThresholdMinutes,
  twoWeekDisplayStats, weeklyDisplayStats,
  planningMode,
  selectedProps,
  focusCrewServiceDelta,
  projectedFocusTotalMinutes, projectedFocusOtMinutes,
  handleWeekDaySelect,
  focusedRoute, mapRef, setSelectedPropertyId,
  fetchFocusContextStats, fetchFocusedRoute,
  url, selectedScenarioId,
  approvedSet, setApprovedRouteDays,
  onSnackbar,
  // Route builder props (passed through from MapTab)
  routeBuilderCandidates,
  candidatesDueOnly,
  showSuggestionPins,
  remainingCapacityMin,
  routeActionPending,
  handlePropertySelect, addToDay,
  SUGGESTION_PIN_COLORS,
  // Capacity drawer toggle
  setDrawerOpen,
  // Property count
  propertyCount, routeCount
}) {
  const [sideTab, setSideTab] = useState('build')

  // Fleet mode: simplified summary
  if (isFleetMode) {
    return (
      <div className="route-sidebar">
        {/* Header */}
        <div className="route-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
              width: 11, height: 11, borderRadius: '50%', flexShrink: 0,
              background: 'var(--df-primary, #1f5e3a)'
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink, #1c2419)', lineHeight: 1.2 }}>
                Fleet Day Summary
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--df-ink-3, #6f7a6b)', marginTop: 1 }}>
                {fleetDayStats?.dayName || getDayName(parseYmd(focusDate)) || '?'}
              </div>
            </div>
            <div className="seg-control" style={{ padding: 2 }} onClick={(e) => e.stopPropagation()}>
              {['estimated', 'actual'].map(m => (
                <button
                  key={m}
                  className={'seg-btn' + (hoursMode === m ? ' active' : '')}
                  onClick={() => setHoursMode(m)}
                  style={{ padding: '3px 8px', fontSize: 10.5, textTransform: 'capitalize' }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Fleet stats grid */}
          {!!fleetDayStats && (
            <div className="stat-grid" style={{ marginBottom: 10 }}>
              {[
                { l: 'Crews', v: fleetDayStats.crewCount },
                { l: 'Stops', v: fleetDayStats.totalStops },
                { l: 'Service', v: formatMinutes(fleetDayStats.totalServiceMin) },
                { l: 'Drive', v: formatMinutes(fleetDayStats.totalDriveMin) },
                { l: 'Total', v: formatMinutes(fleetDayStats.totalTotalMin) },
                { l: 'OT', v: formatMinutes(fleetDayStats.totalOtMin), warn: fleetDayStats.crewsInOt > 0 }
              ].map(s => (
                <div className="stat-cell" key={s.l}>
                  <div className="stat-label">{s.l}</div>
                  <div className="stat-value" style={s.warn ? { color: '#a9341d' } : undefined}>
                    {s.v}
                    {s.l === 'OT' && fleetDayStats.crewsInOt > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 500, color: '#a9341d' }}> ({fleetDayStats.crewsInOt})</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!capacityMatrix && (
            <div style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)' }}>
              No capacity data — select a scenario.
            </div>
          )}

          {!!capacityMatrix && !fleetDayStats && (
            <div style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)' }}>No data for this day.</div>
          )}
        </div>

        {/* Per-route breakdown (scrollable) */}
        {!!fleetDayStats && fleetDayStats.crewDays?.length > 0 && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--df-ink, #1c2419)', padding: '12px 0 8px' }}>
              Per-Route ({fleetDayStats.crewDays.length})
            </div>
            {fleetDayStats.crewDays.map(cd => {
              const pct = Math.round((cd.capacityPct || ((cd.totalMin || 0) / DAILY_OT_THRESHOLD_MIN * 100)) || 0)
              const barColor = capColor(pct)
              return (
                <div
                  key={cd.routeId}
                  style={{
                    padding: '8px 0', borderBottom: '1px solid var(--df-line, #ede6d2)',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    if (cd.routeId) setFocusRouteId(cd.routeId)
                    if (cd.crewId) setFocusCrewId(cd.crewId)
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{
                      fontSize: 12, fontWeight: 600, color: 'var(--df-ink, #1c2419)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 170
                    }}>
                      {cd.routeName || cd.routeId}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--df-ink-3, #6f7a6b)', flexShrink: 0, marginLeft: 4 }}>
                      {cd.stops || 0} stops · {formatMinutes(cd.totalMin || 0)}
                    </span>
                  </div>
                  <div className="cap-gauge-track" style={{ marginTop: 4 }}>
                    <div className="cap-gauge-fill" style={{ width: `${Math.min(100, pct)}%`, background: barColor }} />
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--df-ink-4, #97a094)', marginTop: 2 }}>
                    {pct}% capacity
                    {(cd.otMin || 0) > 0 && <span style={{ color: '#a9341d', fontWeight: 600 }}> +{formatMinutes(cd.otMin)} OT</span>}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Footer */}
        <div className="sidebar-footer">
          {propertyCount || 0} properties · {routeCount || 0} routes
        </div>
      </div>
    )
  }

  // ── Non-fleet: Route Context mode ────────────────────────
  const dayPct = contextSummary
    ? Math.min(120, Math.round((contextSummary.totalMin || 0) / DAILY_OT_THRESHOLD_MIN * 100))
    : 0
  const dayUsedMin = contextSummary?.totalMin || 0
  const dayColor = capColor(dayPct)
  const focusWeekType = getWeekTypeForDate(parseYmd(focusDate))
  const dayName = getDayName(parseYmd(focusDate))
  const isCurrentApproved = dayName && approvedSet?.has(`${focusCrewId}_${dayName}_${focusWeekType}`)
  const stopsCount = focusedRoute?.stops?.length || 0
  const candidatesCount = routeBuilderCandidates?.length || 0

  return (
    <div className="route-sidebar">
      {/* ── ZONE 1: Sticky route header ───────────────────── */}
      <div className="route-header">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
          <span style={{
            width: 11, height: 11, borderRadius: '50%', flexShrink: 0, marginTop: 3,
            background: 'var(--df-primary, #1f5e3a)'
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--df-ink, #1c2419)', lineHeight: 1.2 }}>
              {focusCrewName || 'Select a route'}
            </div>
            {focusCrewId && (
              <div style={{ fontSize: 11.5, color: 'var(--df-ink-3, #6f7a6b)', marginTop: 1 }}>
                {dayName || '?'}, {planningMode}{hoursMode === 'actual' ? ' (actual)' : ''}
                {focusWeekType ? `, Wk ${focusWeekType}` : ''}
              </div>
            )}
          </div>
          <div className="seg-control" style={{ padding: 2 }} onClick={(e) => e.stopPropagation()}>
            {['estimated', 'actual'].map(m => (
              <button
                key={m}
                className={'seg-btn' + (hoursMode === m ? ' active' : '')}
                onClick={() => setHoursMode(m)}
                style={{ padding: '3px 8px', fontSize: 10.5, textTransform: 'capitalize' }}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        {/* Capacity gauge */}
        {!!contextSummary && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--df-ink-3, #6f7a6b)' }}>Day capacity</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--df-ink, #1c2419)' }}>
                {formatMinutes(dayUsedMin)}{' '}
                <span style={{ color: 'var(--df-ink-4, #97a094)', fontWeight: 500 }}>
                  / {formatMinutes(DAILY_OT_THRESHOLD_MIN)} · {dayPct}%
                </span>
              </span>
            </div>
            <div className="cap-gauge-track">
              <div className="cap-gauge-fill" style={{ width: `${Math.min(100, dayPct)}%`, background: dayColor }} />
            </div>
          </div>
        )}

        {!focusCrewId && (
          <div style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)' }}>
            Pick a focus route to see stats.
          </div>
        )}
        {!!focusCrewId && !contextSummary && !loadingFocusContextStats && (
          <div style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)' }}>
            No stats for this context.
          </div>
        )}
        {loadingFocusContextStats && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <CircularProgress size={14} style={{ color: 'var(--df-primary, #1f5e3a)' }} />
            <span style={{ fontSize: 11.5, color: 'var(--df-ink-4, #97a094)' }}>Loading...</span>
          </div>
        )}

        {/* Stats grid */}
        {!!contextSummary && (
          <div className="stat-grid" style={{ marginBottom: 10 }}>
            {[
              { l: 'Stops', v: contextSummary.stops || 0 },
              { l: 'Service', v: formatMinutes(contextSummary.serviceMin || 0) },
              { l: 'Drive', v: formatMinutes(contextSummary.driveMin || 0) },
              { l: 'Total', v: formatMinutes(contextSummary.totalMin || 0) },
              { l: 'OT', v: formatMinutes(contextSummary.otMin || 0), warn: (contextSummary.otMin || 0) > 0 },
              { l: 'Threshold', v: formatMinutes(contextThresholdMinutes) }
            ].map(s => (
              <div className="stat-cell" key={s.l}>
                <div className="stat-label">{s.l}</div>
                <div className="stat-value" style={s.warn ? { color: '#a9341d' } : undefined}>{s.v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Two-week or weekly display */}
        {!!twoWeekDisplayStats && (
          <div style={{
            background: 'var(--df-surface-2, #faf6ee)',
            border: '1px solid var(--df-line, #ede6d2)',
            borderRadius: 10, padding: '12px 14px', marginBottom: 10
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--df-ink, #1c2419)' }}>Two Weeks (Mon-Fri)</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--df-ink, #1c2419)' }}>
                {formatMinutes(twoWeekDisplayStats.totalMin)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 10 }}>
              <span style={twoWeekDisplayStats.totalOtMin > 0 ? { color: '#a9341d', fontWeight: 600 } : { color: 'var(--df-ink-4, #97a094)' }}>
                OT Risk: {formatMinutes(twoWeekDisplayStats.totalOtMin)}
              </span>
              <span style={{ color: 'var(--df-ink-4, #97a094)' }}>Limit: 40h / wk</span>
            </div>
            {twoWeekDisplayStats.groups.map(group => (
              <div key={group.weekType} style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--df-ink, #1c2419)' }}>Week {group.weekType}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--df-ink, #1c2419)' }}>
                    {formatMinutes(group.weekTotalMin)}
                  </span>
                </div>
                <div className="cap-gauge-track" style={{ marginBottom: 4 }}>
                  <div className="cap-gauge-fill" style={{
                    width: `${Math.min(100, (group.weekTotalMin / WEEKLY_OT_THRESHOLD_MIN) * 100)}%`,
                    background: capColor(Math.round(group.weekTotalMin / WEEKLY_OT_THRESHOLD_MIN * 100))
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--df-ink-4, #97a094)', marginBottom: 6 }}>
                  <span style={group.hasOt ? { color: '#a9341d', fontWeight: 600 } : undefined}>
                    OT: {formatMinutes(group.weekOtMin + group.dayOtMin)}
                  </span>
                  <span>Svc {formatMinutes(group.weekServiceMin)} | Drive {formatMinutes(group.weekDriveMin)}</span>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {group.days.map(day => {
                    const isFocused = day.date === focusDate
                    const isDailyOt = Number(day.otMin || 0) > 0
                    return (
                      <button
                        type="button"
                        key={day.date}
                        className="day-chip"
                        style={{
                          border: isFocused ? '1px solid var(--df-ink, #1c2419)' : '1px solid transparent',
                          background: isFocused ? 'var(--df-ink, #1c2419)' : isDailyOt ? '#f7dccf' : 'var(--df-bg-2, #f1ece0)',
                          cursor: 'pointer', fontFamily: 'inherit'
                        }}
                        onClick={() => handleWeekDaySelect(day.date)}
                        title={`Show ${day.dayName} Week ${group.weekType} route`}
                      >
                        <span className="dc-day" style={{
                          color: isFocused ? '#e8ede4' : isDailyOt ? '#a4502f' : 'var(--df-ink-3, #6f7a6b)'
                        }}>{day.dayName?.charAt(0)}</span>
                        <span className="dc-min" style={{
                          color: isFocused ? '#fbf8f1' : isDailyOt ? '#a9341d' : 'var(--df-ink-2, #3d463a)'
                        }}>{formatMinutes(day.totalMin)}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {!twoWeekDisplayStats && !!weeklyDisplayStats && (
          <div style={{
            background: 'var(--df-surface-2, #faf6ee)',
            border: '1px solid var(--df-line, #ede6d2)',
            borderRadius: 10, padding: '12px 14px', marginBottom: 10
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--df-ink, #1c2419)' }}>Week (Mon-Fri)</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--df-ink, #1c2419)' }}>
                {formatMinutes(weeklyDisplayStats.weekTotalMin)}
              </span>
            </div>
            <div className="cap-gauge-track" style={{ marginBottom: 6 }}>
              <div className="cap-gauge-fill" style={{
                width: `${Math.min(100, (weeklyDisplayStats.weekTotalMin / WEEKLY_OT_THRESHOLD_MIN) * 100)}%`,
                background: capColor(Math.round(weeklyDisplayStats.weekTotalMin / WEEKLY_OT_THRESHOLD_MIN * 100))
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 10 }}>
              <span style={weeklyDisplayStats.weekOtMin > 0 ? { color: '#a9341d', fontWeight: 600 } : { color: 'var(--df-ink-4, #97a094)' }}>
                OT: {formatMinutes(weeklyDisplayStats.weekOtMin)}
              </span>
              <span style={{ color: 'var(--df-ink-4, #97a094)' }}>Limit: 40h</span>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {weeklyDisplayStats.days.map(d => {
                const isFocused = d.date === focusDate
                const isDailyOt = d.displayOt > 0
                return (
                  <button
                    type="button"
                    key={d.date}
                    className="day-chip"
                    style={{
                      border: isFocused ? '1px solid var(--df-ink, #1c2419)' : '1px solid transparent',
                      background: isFocused ? 'var(--df-ink, #1c2419)' : isDailyOt ? '#f7dccf' : 'var(--df-bg-2, #f1ece0)',
                      cursor: 'pointer', fontFamily: 'inherit'
                    }}
                    onClick={() => handleWeekDaySelect(d.date)}
                    title={`Show ${d.dayName} route`}
                  >
                    <span className="dc-day" style={{
                      color: isFocused ? '#e8ede4' : isDailyOt ? '#a4502f' : 'var(--df-ink-3, #6f7a6b)'
                    }}>{d.dayName?.charAt(0)}</span>
                    <span className="dc-min" style={{
                      color: isFocused ? '#fbf8f1' : isDailyOt ? '#a9341d' : 'var(--df-ink-2, #3d463a)'
                    }}>{formatMinutes(d.displayTotal)}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Impact box (when property selected) */}
        {!!selectedProps && !!focusCrewId && !!contextSummary && (
          <div style={{
            background: 'var(--df-surface-2, #faf6ee)',
            border: '1px solid var(--df-line, #ede6d2)',
            borderRadius: 10, padding: '12px 14px', marginBottom: 10
          }}>
            {[
              { l: 'Context total', v: `${formatMinutes(contextSummary.totalMin || 0)} > ${formatMinutes(projectedFocusTotalMinutes || 0)}` },
              { l: 'Delta', v: formatDeltaMinutes(focusCrewServiceDelta), c: focusCrewServiceDelta > 0 ? '#a9341d' : focusCrewServiceDelta < 0 ? '#2f7a3e' : undefined },
              { l: 'Projected OT', v: formatMinutes(projectedFocusOtMinutes || 0), c: projectedFocusOtMinutes > 0 ? '#a9341d' : undefined }
            ].map(r => (
              <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
                <span style={{ fontSize: 12, color: 'var(--df-ink-3, #6f7a6b)' }}>{r.l}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: r.c || 'var(--df-ink, #1c2419)' }}>{r.v}</span>
              </div>
            ))}
            {projectedFocusOtMinutes > 0 && (
              <div style={{ fontSize: 11, color: '#a9341d', fontWeight: 600, marginTop: 4 }}>
                Exceeds threshold by {formatMinutes(projectedFocusOtMinutes)}
              </div>
            )}
          </div>
        )}

        {/* Status + actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          {!!contextSummary && setDrawerOpen && (
            <button
              className="cap-details-btn"
              onClick={() => setDrawerOpen(true)}
            >
              <BarChartIcon style={{ fontSize: 16 }} /> Capacity details
              <ChevronRightIcon style={{ fontSize: 16, color: 'var(--df-ink-4, #97a094)' }} />
            </button>
          )}
          {!!selectedScenarioId && !!focusCrewId && !!focusDate && dayName &&
            ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(dayName) && (
            <button
              className="approve-btn"
              style={{
                marginLeft: 'auto',
                background: isCurrentApproved ? '#2f7a3e' : 'var(--df-primary, #1f5e3a)'
              }}
              onClick={async () => {
                try {
                  const resp = await axios.post(
                    `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/approve-route-day`,
                    { crewId: focusCrewId, routeId: focusRouteId || null, dayOfWeek: dayName, weekType: focusWeekType, unapprove: isCurrentApproved }
                  )
                  setApprovedRouteDays(resp.data?.approvedRouteDays || [])
                  if (onSnackbar) {
                    onSnackbar(
                      isCurrentApproved
                        ? `Unapproved ${focusCrewName} ${dayName} (Wk ${focusWeekType})`
                        : `Approved ${focusCrewName} ${dayName} (Wk ${focusWeekType})`,
                      'success'
                    )
                  }
                } catch (err) {
                  if (onSnackbar) onSnackbar(err.response?.data?.message || 'Failed to update approval', 'error')
                }
              }}
            >
              <CheckIcon style={{ fontSize: 14 }} />
              {isCurrentApproved ? `Unapprove (Wk ${focusWeekType})` : `Approve Day`}
            </button>
          )}
        </div>
      </div>

      {/* ── ZONE 2: Segmented control ─────────────────────── */}
      {!!focusCrewId && (
        <div style={{ padding: '12px 14px 10px', flexShrink: 0 }}>
          <div className="seg-control">
            <button
              className={'seg-btn' + (sideTab === 'build' ? ' active' : '')}
              onClick={() => setSideTab('build')}
            >
              Build <span className="seg-count">{candidatesCount}</span>
            </button>
            <button
              className={'seg-btn' + (sideTab === 'stops' ? ' active' : '')}
              onClick={() => setSideTab('stops')}
            >
              Stops <span className="seg-count">{stopsCount}</span>
            </button>
          </div>
        </div>
      )}

      {/* ── ZONE 3: Scrollable work area ──────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 14px 16px' }}>
        {sideTab === 'build' && focusCrewId && (
          <div className="page-enter">
            {/* Toggles */}
            <div style={{ display: 'flex', gap: 16, padding: '4px 2px 12px' }}>
              <label className="rw-toggle">
                <span className={'toggle-track' + (candidatesDueOnly ? ' on' : '')} style={{
                  width: 28, height: 16, borderRadius: 99, position: 'relative',
                  background: candidatesDueOnly ? 'var(--df-primary, #1f5e3a)' : 'var(--df-line, #cec3a5)',
                  transition: 'background 0.15s', flexShrink: 0, display: 'inline-block'
                }}>
                  <span style={{
                    position: 'absolute', top: 2, left: candidatesDueOnly ? 14 : 2,
                    width: 12, height: 12, borderRadius: '50%', background: '#fff',
                    transition: 'left 0.15s'
                  }} />
                </span>
                Due today only
              </label>
              <label className="rw-toggle">
                <span className={'toggle-track' + (showSuggestionPins ? ' on' : '')} style={{
                  width: 28, height: 16, borderRadius: 99, position: 'relative',
                  background: showSuggestionPins ? 'var(--df-primary, #1f5e3a)' : 'var(--df-line, #cec3a5)',
                  transition: 'background 0.15s', flexShrink: 0, display: 'inline-block'
                }}>
                  <span style={{
                    position: 'absolute', top: 2, left: showSuggestionPins ? 14 : 2,
                    width: 12, height: 12, borderRadius: '50%', background: '#fff',
                    transition: 'left 0.15s'
                  }} />
                </span>
                Show on map
              </label>
            </div>

            {/* Candidate cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(candidatesDueOnly ? routeBuilderCandidates.filter(c => c.dueToday) : routeBuilderCandidates || [])
                .map((candidate, idx) => (
                  <CandidateCard
                    key={candidate.aspirePropertyId}
                    candidate={candidate}
                    idx={idx}
                    pinColor={(SUGGESTION_PIN_COLORS || [])[idx % 10]}
                    showSuggestionPins={showSuggestionPins}
                    remainingCapacityMin={remainingCapacityMin}
                    selectedScenarioId={selectedScenarioId}
                    routeActionPending={routeActionPending}
                    handlePropertySelect={handlePropertySelect}
                    addToDay={addToDay}
                  />
                ))}
              {(!routeBuilderCandidates || routeBuilderCandidates.length === 0) && (
                <div style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)', padding: '8px 0' }}>
                  No candidates found.
                </div>
              )}
            </div>
          </div>
        )}

        {sideTab === 'stops' && focusCrewId && (
          <div className="page-enter">
            <RouteStopsList
              focusedRoute={focusedRoute}
              mapRef={mapRef}
              setSelectedPropertyId={setSelectedPropertyId}
            />
            <button
              className="reload-route-btn"
              style={{ marginTop: 12 }}
              onClick={() => {
                fetchFocusContextStats()
                fetchFocusedRoute()
              }}
              disabled={!selectedScenarioId || !focusCrewId}
            >
              <RefreshIcon style={{ fontSize: 16 }} />
              Reload Route
            </button>
          </div>
        )}

        {!focusCrewId && (
          <div style={{ fontSize: 12, color: 'var(--df-ink-4, #97a094)', padding: '16px 0', textAlign: 'center' }}>
            Select a route or crew to see route builder.
          </div>
        )}
      </div>

      {/* ── ZONE 4: Footer ────────────────────────────────── */}
      <div className="sidebar-footer">
        {propertyCount || 0} properties · {routeCount || 0} routes
      </div>
    </div>
  )
}
